import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import {
  useChannels,
  usePostTemplate,
  useCreatePostTemplate,
  useUpdatePostTemplate,
} from '@/hooks/useSocial';
import { Card, Button, Input, Textarea } from '@solid/ui';
import type {
  SocialNetwork,
  PubChannel,
  PubPostTemplateVariable,
} from '@/types';

const NETWORK_LABELS: Record<SocialNetwork, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  TWITTER: 'X / Twitter',
  LINKEDIN: 'LinkedIn',
  PINTEREST: 'Pinterest',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  GOOGLE_BUSINESS: 'Google Business',
  TELEGRAM: 'Telegram',
  WORDPRESS: 'WordPress',
  MASTODON: 'Mastodon',
  THREADS: 'Threads',
  BLUESKY: 'Bluesky',
};

const EMPTY_VARIABLE: PubPostTemplateVariable = {
  key: '',
  label: '',
  required: false,
  defaultValue: '',
};

export default function SocialTemplateEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { data: channels = [], isLoading: channelsLoading } = useChannels();
  const { data: existingTemplate } = usePostTemplate(id!);
  const createMutation = useCreatePostTemplate();
  const updateMutation = useUpdatePostTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultContent, setDefaultContent] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [showOverrides, setShowOverrides] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [newMediaUrl, setNewMediaUrl] = useState('');
  const [variables, setVariables] = useState<PubPostTemplateVariable[]>([]);

  // Load existing template data
  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name || '');
      setDescription(existingTemplate.description || '');
      setDefaultContent(existingTemplate.content.default || '');
      setOverrides(existingTemplate.content.overrides || {});
      setShowOverrides(
        Object.keys(existingTemplate.content.overrides || {}).length > 0,
      );
      setSelectedChannels(existingTemplate.channelIds || []);
      setMediaUrls(existingTemplate.mediaUrls || []);
      setVariables(existingTemplate.variables || []);
    }
  }, [existingTemplate]);

  const activeChannels = channels.filter((ch) => ch.isActive);

  const toggleChannel = (channelId: string) => {
    setSelectedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId],
    );
  };

  const addMediaUrl = () => {
    const url = newMediaUrl.trim();
    if (url) {
      setMediaUrls((prev) => [...prev, url]);
      setNewMediaUrl('');
    }
  };

  const removeMediaUrl = (index: number) => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Variables helpers
  const addVariable = () => {
    setVariables((prev) => [...prev, { ...EMPTY_VARIABLE }]);
  };

  const updateVariable = (
    index: number,
    field: keyof PubPostTemplateVariable,
    value: string | boolean,
  ) => {
    setVariables((prev) =>
      prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    );
  };

  const removeVariable = (index: number) => {
    setVariables((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim() || !defaultContent.trim()) return;

    const contentPayload: any = { default: defaultContent.trim() };
    const activeOverrides: Record<string, string> = {};
    for (const [key, val] of Object.entries(overrides)) {
      if (val.trim()) activeOverrides[key] = val.trim();
    }
    if (Object.keys(activeOverrides).length > 0) {
      contentPayload.overrides = activeOverrides;
    }

    // Clean variables: only include those with at least a key
    const cleanVariables = variables
      .filter((v) => v.key.trim())
      .map((v) => ({
        key: v.key.trim(),
        label: v.label.trim() || v.key.trim(),
        required: v.required,
        defaultValue: v.defaultValue || undefined,
      }));

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      content: contentPayload,
      channelIds: selectedChannels,
      mediaUrls: mediaUrls.length > 0 ? mediaUrls : [],
      variables: cleanVariables,
    };

    if (isEditing) {
      await updateMutation.mutateAsync({ id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }

    navigate('/admin/social/templates');
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Get unique networks from selected channels for override options
  const selectedNetworks = new Set(
    activeChannels
      .filter((ch) => selectedChannels.includes(ch.id))
      .map((ch) => ch.network),
  );

  return (
    <div className="page-enter space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/social/templates')}
          className="p-2 rounded-lg hover:bg-grey-100 transition-colors"
          aria-label="Back to social templates"
        >
          <ArrowLeft className="w-5 h-5 text-grey-500" />
        </button>
        <h1 className="text-heading-2 text-secondary">
          {isEditing ? 'Edit Template' : 'New Template'}
        </h1>
      </div>

      {/* Name & Description */}
      <Card padding="md" className="space-y-4">
        <h3 className="text-heading-4 text-secondary">Template Info</h3>
        <Input
          label="Name"
          placeholder="e.g. Weekly Case Study Post"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Description"
          placeholder="Short description of when to use this template"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Card>

      {/* Default content */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-2">Content</h3>
        <p className="text-sm text-grey-400 mb-4">
          Use <code className="bg-grey-100 px-1 rounded">{'{{variable}}'}</code>{' '}
          for merge fields. For LinkedIn tags use{' '}
          <code className="bg-grey-100 px-1 rounded">{'@[{{name}}]({{linkedinUrn}})'}</code>{' '}
          inline.
        </p>
        <Textarea
          label="Default Content"
          placeholder="Write your template content here..."
          rows={5}
          value={defaultContent}
          onChange={(e) => setDefaultContent(e.target.value)}
        />

        {/* Per-platform overrides */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowOverrides(!showOverrides)}
            className="text-sm text-primary hover:text-primary-700 font-medium"
          >
            {showOverrides
              ? 'Hide platform-specific content'
              : 'Customize per platform'}
          </button>

          {showOverrides && (
            <div className="mt-3 space-y-3">
              {Array.from(selectedNetworks).map((network) => (
                <Textarea
                  key={network}
                  label={NETWORK_LABELS[network]}
                  placeholder={`Custom content for ${NETWORK_LABELS[network]} (optional)`}
                  rows={3}
                  value={overrides[network] || ''}
                  onChange={(e) =>
                    setOverrides((prev) => ({
                      ...prev,
                      [network]: e.target.value,
                    }))
                  }
                />
              ))}
              {selectedNetworks.size === 0 && (
                <p className="text-sm text-grey-400">
                  Select channels below to customize per platform
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Channel selection */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-4">
          Default Channels
        </h3>
        <p className="text-sm text-grey-400 mb-3">
          Channels pre-selected when creating a post from this template
        </p>
        {channelsLoading ? (
          <p className="text-grey-400 text-sm">Loading channels...</p>
        ) : activeChannels.length === 0 ? (
          <p className="text-grey-400 text-sm">
            No channels synced. Go to Settings &rarr; Social Channels to sync
            from Publer.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeChannels.map((channel) => (
              <ChannelCheckbox
                key={channel.id}
                channel={channel}
                checked={selectedChannels.includes(channel.id)}
                onToggle={() => toggleChannel(channel.id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Media URL Templates */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-2">
          Media URL Templates
        </h3>
        <p className="text-sm text-grey-400 mb-3">
          Add media URLs or URL templates. You can use{' '}
          <code className="bg-grey-100 px-1 rounded">{'{{variable}}'}</code>{' '}
          syntax.
        </p>

        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/{{slug}}/hero.jpg"
            value={newMediaUrl}
            onChange={(e) => setNewMediaUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMediaUrl();
              }
            }}
          />
          <Button
            variant="ghost"
            onClick={addMediaUrl}
            disabled={!newMediaUrl.trim()}
            aria-label="Add media URL"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {mediaUrls.length > 0 && (
          <div className="mt-3 space-y-2">
            {mediaUrls.map((url, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-grey-50 rounded-lg px-3 py-2"
              >
                <span className="text-sm text-secondary truncate flex-1">
                  {url}
                </span>
                <button
                  onClick={() => removeMediaUrl(i)}
                  className="text-grey-400 hover:text-error shrink-0"
                  aria-label="Remove media URL"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Variables Schema */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-heading-4 text-secondary">Variables</h3>
          <Button variant="ghost" size="sm" onClick={addVariable}>
            <Plus className="w-4 h-4 mr-1" />
            Add Variable
          </Button>
        </div>
        <p className="text-sm text-grey-400 mb-4">
          Define the variables used in this template. These are prompted when
          creating a post from the template.
        </p>

        {variables.length === 0 && (
          <p className="text-sm text-grey-400 italic">No variables defined</p>
        )}

        <div className="space-y-3">
          {variables.map((variable, i) => (
            <div
              key={i}
              className="flex items-start gap-3 bg-grey-50 rounded-lg p-3"
            >
              <div className="grid grid-cols-2 gap-3 flex-1">
                <Input
                  placeholder="key (e.g. clientName)"
                  value={variable.key}
                  onChange={(e) => updateVariable(i, 'key', e.target.value)}
                />
                <Input
                  placeholder="Label (e.g. Client Name)"
                  value={variable.label}
                  onChange={(e) => updateVariable(i, 'label', e.target.value)}
                />
                <Input
                  placeholder="Default value (optional)"
                  value={variable.defaultValue || ''}
                  onChange={(e) =>
                    updateVariable(i, 'defaultValue', e.target.value)
                  }
                />
                <label className="flex items-center gap-2 text-sm text-secondary">
                  <input
                    type="checkbox"
                    checked={variable.required}
                    onChange={(e) =>
                      updateVariable(i, 'required', e.target.checked)
                    }
                    className="rounded border-grey-300 text-primary focus:ring-primary"
                  />
                  Required
                </label>
              </div>
              <button
                onClick={() => removeVariable(i)}
                className="text-grey-400 hover:text-error mt-2 shrink-0"
                aria-label="Remove variable"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/social/templates')}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSaving}
          disabled={!name.trim() || !defaultContent.trim()}
        >
          {isEditing ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
}

function ChannelCheckbox({
  channel,
  checked,
  onToggle,
}: {
  channel: PubChannel;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        checked
          ? 'border-primary bg-primary/5'
          : 'border-grey-200 hover:border-grey-300'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="rounded border-grey-300 text-primary focus:ring-primary"
      />
      {channel.avatar && (
        <img
          src={channel.avatar}
          alt=""
          className="w-6 h-6 rounded-full object-cover"
        />
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-secondary truncate">
          {channel.name}
        </p>
        <p className="text-xs text-grey-400">
          {NETWORK_LABELS[channel.network]}
        </p>
      </div>
    </label>
  );
}

